// src/utils/bookingDomain.js
//
// SnoPro Accelerator F — the PURE booking normalisation domain. This module is the ONE canonical
// implementation of the booking/request read-normalisers. It has NO Firestore, NO React, NO
// notifications, NO AI — only pure scheduleModel helpers — so the whole partner-integration domain
// graph (partnerBookingProcessor → sourceIntake → clientIdentity → bookingCase → bookingDomain) is
// importable in a plain Node/firebase-admin runtime (a Cloud Run partner service) WITHOUT dragging in
// the browser Firebase client SDK.
//
// `src/utils/booking.js` re-exports these functions, so every existing `import { normalizeBooking,
// normalizeBookingRequest, ... } from './booking'` consumer keeps working unchanged; there is exactly
// ONE implementation of each normaliser, here.

import {
  normalizeEntryType,
  normalizePaymentState,
  normalizeCancellationResolution,
  deriveDurationCategory,
  parseAdditionalGuests,
} from './scheduleModel';

// ── tiny pure scalar helpers (shared by the normalisers below) ───────────────────
export function normalizeString(value = '') {
  return String(value || '').trim();
}

export function normalizeEmail(value = '') {
  return normalizeString(value).toLowerCase();
}

export function normalizeArray(values = []) {
  if (Array.isArray(values)) return values.map((value) => normalizeString(value)).filter(Boolean);
  return normalizeString(values)
    .split(',')
    .map((value) => value.trim())
    .filter(Boolean);
}

export function normalizeNumber(value, fallback = null) {
  if (value === '' || value === null || typeof value === 'undefined') return fallback;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
}

export function normalizeBookingRequest(record = {}, fallbackId = '') {
  const id = record.id || record.bookingRequestId || fallbackId || '';

  return {
    id,
    bookingRequestId: id,
    organizationId: record.organizationId || '',
    instructorId: record.instructorId || '',
    requesterUserId: record.requesterUserId || '',
    requesterName: normalizeString(record.requesterName || ''),
    requesterEmail: normalizeString(record.requesterEmail || ''),
    requesterEmailNormalized: normalizeEmail(record.requesterEmailNormalized || record.requesterEmail || ''),
    requesterPhone: normalizeString(record.requesterPhone || ''),
    serviceId: record.serviceId || '',
    serviceNameSnapshot: normalizeString(record.serviceNameSnapshot || ''),
    serviceDurationSnapshot: normalizeNumber(record.serviceDurationSnapshot, null),
    servicePricingSnapshot: normalizeNumber(record.servicePricingSnapshot, null),
    servicePackageEligible: Boolean(record.servicePackageEligible),
    applicablePackageIds: normalizeArray(record.applicablePackageIds || []),
    discipline: normalizeString(record.discipline || ''),
    serviceType: normalizeString(record.serviceType || ''),
    requestedStartAt: record.requestedStartAt || null,
    requestedEndAt: record.requestedEndAt || null,
    timezone: normalizeString(record.timezone || 'UTC'),
    status: normalizeString(record.status || 'pending'),
    notes: normalizeString(record.notes || ''),
    sourceType: normalizeString(record.sourceType || 'public_profile'),
    handledByUserId: record.handledByUserId || '',
    handledAt: record.handledAt || null,
    externalCalendarRef: normalizeString(record.externalCalendarRef || ''),
    createdAt: record.createdAt || null,
    updatedAt: record.updatedAt || null,
  };
}

export function normalizeBookingChangeRequest(record = {}) {
  const safeRecord = record || {};
  const type = normalizeString(safeRecord.type || '');
  const status = normalizeString(safeRecord.status || '');

  if (!type && !status && !safeRecord.requestedStartAt && !safeRecord.requestedEndAt && !safeRecord.note) {
    return null;
  }

  return {
    type,
    status: status || 'pending',
    requestedStartAt: safeRecord.requestedStartAt || null,
    requestedEndAt: safeRecord.requestedEndAt || null,
    requestedByUserId: safeRecord.requestedByUserId || '',
    requestedByName: normalizeString(safeRecord.requestedByName || ''),
    note: normalizeString(safeRecord.note || ''),
    createdAt: safeRecord.createdAt || null,
    updatedAt: safeRecord.updatedAt || null,
    resolvedAt: safeRecord.resolvedAt || null,
    resolvedByUserId: safeRecord.resolvedByUserId || '',
    resolutionNote: normalizeString(safeRecord.resolutionNote || ''),
  };
}

export function normalizeBookingCalendarState(record = {}) {
  const normalizedStatus = normalizeString(record.syncStatus || '');
  return {
    externalCalendarRef: normalizeString(record.externalCalendarRef || ''),
    externalEventId: normalizeString(record.externalEventId || ''),
    calendarProvider: normalizeString(record.calendarProvider || ''),
    syncStatus: normalizedStatus === 'not_synced' ? 'not_connected' : (normalizedStatus || 'not_connected'),
    syncAttemptCount: normalizeNumber(record.syncAttemptCount, 0) || 0,
    lastSyncedAt: record.lastSyncedAt || null,
    syncErrorMessage: normalizeString(record.syncErrorMessage || ''),
  };
}

export function normalizeBookingCommercialState(record = {}) {
  const depositRequired = Boolean(record.depositRequired);
  return {
    priceSnapshot: normalizeNumber(record.priceSnapshot, null),
    currency: normalizeString(record.currency || 'USD'),
    paymentStatus: normalizeString(record.paymentStatus || 'unpaid'),
    billingSourceType: normalizeString(record.billingSourceType || ''),
    billingSourceId: normalizeString(record.billingSourceId || ''),
    packageId: normalizeString(record.packageId || ''),
    customerMembershipId: normalizeString(record.customerMembershipId || ''),
    packageCoverageAmount: normalizeNumber(record.packageCoverageAmount, 0) || 0,
    packageCoverageStatus: normalizeString(record.packageCoverageStatus || 'none'),
    depositAmountSnapshot: normalizeNumber(record.depositAmountSnapshot, null),
    depositRequired,
    depositStatus: normalizeString(record.depositStatus || (depositRequired ? 'pending' : 'not_required')),
    amountPaid: normalizeNumber(record.amountPaid, 0) || 0,
    outstandingBalance: normalizeNumber(record.outstandingBalance, null),
  };
}

export function normalizeBooking(record = {}, fallbackId = '') {
  const id = record.id || record.bookingId || fallbackId || '';
  const calendarState = normalizeBookingCalendarState(record);
  const commercialState = normalizeBookingCommercialState(record);

  return {
    id,
    bookingId: id,
    organizationId: record.organizationId || '',
    instructorId: record.instructorId || '',
    // Pass 63 — canonical business client identity (DISTINCT from Firebase Auth uid and from the
    // booking CASE). A child booking materialised from a case inherits the case's stable clientId so
    // one customer stays one client across seasons/records even when contact details later change.
    clientId: normalizeString(record.clientId || ''),
    requesterUserId: record.requesterUserId || '',
    requesterName: normalizeString(record.requesterName || ''),
    requesterEmail: normalizeString(record.requesterEmail || ''),
    requesterPhone: normalizeString(record.requesterPhone || ''),
    bookingRequestId: record.bookingRequestId || '',
    // Pass 50A: canonical case→line→slot linkage must survive read/normalise.
    requestLineId: record.requestLineId || '',
    requestSlotId: record.requestSlotId || '',
    requestSlotIndex: record.requestSlotIndex ?? null,
    // Accelerator C — source-neutral external SESSION lineage (provenance, NOT identity/doc-id). Must survive the
    // canonical read so booking.changed/cancelled can deterministically resolve the exact external session.
    sourceExternalSessionId: normalizeString(record.sourceExternalSessionId || ''),
    serviceId: record.serviceId || '',
    serviceNameSnapshot: normalizeString(record.serviceNameSnapshot || ''),
    serviceDurationSnapshot: normalizeNumber(record.serviceDurationSnapshot, null),
    servicePricingSnapshot: normalizeNumber(record.servicePricingSnapshot, null),
    servicePackageEligible: Boolean(record.servicePackageEligible),
    applicablePackageIds: normalizeArray(record.applicablePackageIds || []),
    discipline: normalizeString(record.discipline || ''),
    serviceType: normalizeString(record.serviceType || ''),
    startAt: record.startAt || null,
    endAt: record.endAt || null,
    timezone: normalizeString(record.timezone || 'UTC'),
    status: normalizeString(record.status || 'confirmed'),
    notes: normalizeString(record.notes || ''),
    locationLabel: normalizeString(record.locationLabel || ''),
    meetingType: normalizeString(record.meetingType || 'in_person'),
    // Pass 82E — structured operating-area / service-location / meeting-point ids (org-namespaced doc ids; default ''
    // = unresolved/needs-area). These are the STRUCTURED operational interpretation; `locationLabel` remains the raw
    // (source-owned, for a source booking) free-text meeting/pickup value and is never destroyed or fuzzy-matched.
    operatingAreaId: normalizeString(record.operatingAreaId || ''),
    serviceLocationId: normalizeString(record.serviceLocationId || ''),
    meetingPointId: normalizeString(record.meetingPointId || ''),
    sourceType: normalizeString(record.sourceType || 'booking_request'),
    handledByUserId: record.handledByUserId || '',
    lessonId: record.lessonId || '',
    videoId: record.videoId || '',
    feedbackId: record.feedbackId || '',
    aiReviewId: record.aiReviewId || '',
    linkedDrillIds: Array.isArray(record.linkedDrillIds) ? record.linkedDrillIds.filter(Boolean) : [],
    followUpRecommended: Boolean(record.followUpRecommended),
    followUpBookingId: record.followUpBookingId || '',
    requesterChangeRequest: normalizeBookingChangeRequest(record.requesterChangeRequest),
    // Assignment / reassignment audit fields — written by assignInstructorToBooking.
    // Previously these were dropped on every normalized read (a fragile split that
    // forced consumers to read raw docs). Now preserved through normalization.
    instructorNameSnapshot: normalizeString(record.instructorNameSnapshot || ''),
    previousInstructorId: record.previousInstructorId || '',
    reassignedAt: record.reassignedAt || null,
    reassignedByUserId: record.reassignedByUserId || '',
    assignmentSource: normalizeString(record.assignmentSource || ''),
    // Operational schedule fields (instructor-visible; populated by import / ops).
    groupSize: normalizeNumber(record.groupSize, null),
    language: normalizeString(record.language || ''),
    transportInfo: normalizeString(record.transportInfo || ''),
    // internalNotes is intentionally NOT part of the booking doc — it is staff-only
    // and lives in `bookingInternal/{bookingId}` (read via getBookingInternal),
    // because Firestore cannot hide a single field from a client that can read the
    // booking document.
    // ── Pass 43: SAS workbook model (all additive) ──
    // Schedule entry type — client_booking (default) OR internal event (training/
    // meeting/admin/travel/blocked/other). Internal events reuse this booking doc.
    scheduleEntryType: normalizeEntryType(record.scheduleEntryType),
    // Sam's "Booking Type" (e.g. "MOS - Full Day") + derived duration category.
    bookingType: normalizeString(record.bookingType || ''),
    durationCategory: normalizeString(record.durationCategory || deriveDurationCategory(record.bookingType || '')),
    skillLevel: normalizeString(record.skillLevel || ''),
    // Commercial state (SEPARATE from scheduling `status` and from billing `paymentStatus`).
    paymentState: normalizePaymentState(record.paymentState),
    // Cancellation financial resolution (SEPARATE from status === 'canceled').
    cancellationResolution: normalizeCancellationResolution(record.cancellationResolution),
    // Booking party (item 8) — structured lead + additional guests. Falls back to
    // the legacy requester fields so nothing is lost.
    leadGuest: {
      fullName: normalizeString(record.leadGuest?.fullName || record.requesterName || ''),
      dob: normalizeString(record.leadGuest?.dob || ''),
      email: normalizeString(record.leadGuest?.email || record.requesterEmail || ''),
      phone: normalizeString(record.leadGuest?.phone || record.requesterPhone || ''),
    },
    additionalGuests: Array.isArray(record.additionalGuests)
      ? record.additionalGuests.map((g) => ({ name: normalizeString(g?.name || ''), dob: normalizeString(g?.dob || '') })).filter((g) => g.name)
      : parseAdditionalGuests(record.additionalGuests || ''),
    // Accommodation (item 9) — distinct from meeting/pickup `locationLabel`.
    accommodationName: normalizeString(record.accommodationName || ''),
    accommodationAddress: normalizeString(record.accommodationAddress || ''),
    accommodationMapUrl: normalizeString(record.accommodationMapUrl || ''),
    // External/commercial identifiers (item 10) — kept DISTINCT.
    roomBossId: normalizeString(record.roomBossId || ''),
    invoiceNumber: normalizeString(record.invoiceNumber || ''),
    // Referral attribution (item 11) — no financial rules yet.
    isReferral: Boolean(record.isReferral) || Boolean(record.referredByName) || Boolean(record.referredByInstructorId),
    referredByName: normalizeString(record.referredByName || ''),
    referredByInstructorId: normalizeString(record.referredByInstructorId || ''),
    // Timesheet/pay foundation (item 13) — model only, no UI this cycle.
    completedHours: normalizeNumber(record.completedHours, null),
    rateType: normalizeString(record.rateType || ''), // teaching | non_teaching | request_teaching
    payExtras: normalizeNumber(record.payExtras, null),
    // Season identity (Pass 46) — additive; every booking attributable to one season.
    // Does NOT replace startAt. Historical seasons render read-only.
    seasonId: normalizeString(record.seasonId || ''),
    importedFromSeasonSeed: Boolean(record.importedFromSeasonSeed),
    // Provenance / idempotent-import identity (Pass 42).
    source: normalizeString(record.source || record.sourceType || ''),
    externalId: normalizeString(record.externalId || ''),
    externalSource: normalizeString(record.externalSource || ''),
    importBatchId: normalizeString(record.importBatchId || ''),
    lastImportedAt: record.lastImportedAt || null,
    // ── Pass 49A §1/§2: canonical runtime + provenance fields written by production
    // migrations/importers must survive this read boundary. Auth identity (Firebase UID)
    // is kept SEPARATE from the canonical member identity (instructorId) — never merged.
    instructorAuthUserId: record.instructorAuthUserId || null,
    previousInstructorAuthUserId: record.previousInstructorAuthUserId || null,
    sourceInstructorLabel: normalizeString(record.sourceInstructorLabel || ''),
    assignmentState: normalizeString(record.assignmentState || ''),
    activityType: normalizeString(record.activityType || ''),
    serviceDate: normalizeString(record.serviceDate || ''),
    hasOperationalTime: record.hasOperationalTime !== undefined ? !!record.hasOperationalTime : undefined,
    timingConfidence: normalizeString(record.timingConfidence || ''),
    needsReview: record.needsReview === true ? true : (record.needsReview === false ? false : undefined),
    needsReviewReason: normalizeString(record.needsReviewReason || ''),
    readiness: normalizeString(record.readiness || ''),
    canonicalRecord: record.canonicalRecord === true ? true : undefined,
    historicalImport: record.historicalImport === true ? true : undefined,
    importedFromWorkbook: record.importedFromWorkbook === true ? true : undefined,
    bootstrapImport: record.bootstrapImport === true ? true : undefined,
    initialImport: record.initialImport === true ? true : undefined,
    sourceRecordId: normalizeString(record.sourceRecordId || ''),
    sourceContentHash: normalizeString(record.sourceContentHash || ''),
    sourceFilename: normalizeString(record.sourceFilename || ''),
    sourceSheet: normalizeString(record.sourceSheet || ''),
    sourceWorkbookRow: record.sourceWorkbookRow != null ? record.sourceWorkbookRow : undefined,
    sourceRecordIndex: record.sourceRecordIndex != null ? record.sourceRecordIndex : undefined,
    classificationConfidence: normalizeString(record.classificationConfidence || ''),
    classificationFamily: normalizeString(record.classificationFamily || ''),
    // Pass 46H source-truthed temporal/rate snapshots (worked hours when clock times are
    // unreliable) — these are canonical and must survive the read boundary.
    hoursSnapshot: record.hoursSnapshot != null ? record.hoursSnapshot : undefined,
    ratePHSnapshot: record.ratePHSnapshot != null ? record.ratePHSnapshot : undefined,
    durationMinutesSnapshot: record.durationMinutesSnapshot != null ? record.durationMinutesSnapshot : undefined,
    restoredMissingRow: record.restoredMissingRow === true ? true : undefined,
    additionalSourceRecordIds: Array.isArray(record.additionalSourceRecordIds) ? record.additionalSourceRecordIds : undefined,
    duplicateSourceReason: normalizeString(record.duplicateSourceReason || ''),
    migrationType: normalizeString(record.migrationType || ''),
    migrationVersion: normalizeString(record.migrationVersion || ''),
    // ── Pass 58F4: LIFECYCLE / RETIREMENT provenance — the canonical fields written by the season
    // rebaseline retirement writer (utils/sheetSync/seasonRebaseline.js). These MUST survive the read
    // boundary: without them isLiveBooking() cannot tell a retired/superseded placeholder from live work,
    // and retired bootstrap rows leak back into Unassigned/Day/Week/metrics. `active` is TRI-STATE —
    // NEVER default a missing value to false (legacy docs have no `active` and must stay live).
    active: record.active === false ? false : (record.active === true ? true : undefined),
    lifecycleState: normalizeString(record.lifecycleState || ''),
    rebaselineSuperseded: record.rebaselineSuperseded === true ? true : undefined,
    retiredAt: record.retiredAt || null,
    retiredReason: normalizeString(record.retiredReason || ''),
    retiredExternalId: normalizeString(record.retiredExternalId || ''),
    supersededBySnapshot: normalizeString(record.supersededBySnapshot || ''),
    ...calendarState,
    ...commercialState,
    createdAt: record.createdAt || null,
    updatedAt: record.updatedAt || null,
  };
}
